import React, { createContext, useContext } from 'react';

import Button from 'CommonComponents/Button';

/**
 * What the wizard hands every step page without each page forwarding it: the
 * "go to the previous visible step" handler (null on the first step) and the
 * step's position among the visible steps.
 */
export const StepFrameContext = createContext({ onBack: null, position: null, total: null });

// Optional steps tied to a module or to what was switched on earlier. The
// wizard labels them differently from the plain optional steps.
const CONDITIONAL = {
    proc: { crumb: () => $t('Procurement & Contract Management module'), badge: () => $t('Needed before first procurement') },
    approvals: { crumb: () => $t('Approvals'), badge: () => $t('Follows what you switched on') },
    radio: { crumb: () => $t('R.A.D.I.O. module'), badge: () => $t('Needed before first entry') },
};

function frameLabels(step, position, total) {
    const stepOf = $t('Step {{position}} of {{total}}', { position, total });

    if (step.group === 'required') {
        return { crumb: stepOf, badge: $t('Required'), badgeClass: 'tw-bg-blue-25 tw-text-blue', hint: $t('Required') };
    }

    const conditional = CONDITIONAL[step.key];

    if (conditional) {
        return {
            crumb: conditional.crumb(),
            badge: conditional.badge(),
            badgeClass: 'tw-bg-green-25 tw-text-dark-green',
            hint: $t('Needed before first use'),
        };
    }

    return {
        crumb: stepOf,
        badge: $t('Optional'),
        badgeClass: 'tw-bg-charcoal-10 tw-text-charcoal-75 tw-border tw-border-charcoal-15',
        hint: $t('Optional — do any time'),
    };
}

/**
 * The chrome every step page renders around its own content: crumb, heading
 * with its badge, the read-only badge, an optional lede, and the actions.
 */
export default function StepShell({ step, lede, children, deferring, saving, onDefer, onNext, nextLabel, nextDisabled }) {
    const { onBack, position, total } = useContext(StepFrameContext);
    const labels = frameLabels(step, position, total);

    return (
        <section className="tw-min-w-0 tw-px-6 tw-py-6.5 tw-rounded-lg tw-bg-white tw-shadow-primary">
            <p className="tw-mb-1.5 tw-text-xs tw-font-medium tw-text-charcoal-50">{labels.crumb}</p>
            <div className="tw-flex tw-justify-between tw-items-start tw-gap-x-4">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <h2 className="table__header tw-text-2.5xl tw-font-bold tw-mb-0">{step.label}</h2>
                    <span className={`tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide ${labels.badgeClass}`}>
                        {labels.badge}
                    </span>
                </div>
                {step.read_only && (
                    <span className="tw-px-2 tw-py-1 tw-rounded tw-bg-charcoal-15 tw-text-2xs tw-font-medium tw-text-charcoal-75">{$t('View only')}</span>
                )}
            </div>

            {lede && <p className="tw-mt-4 tw-text-base tw-text-charcoal-75">{lede}</p>}

            <div className="tw-mt-6">{children}</div>

            <div className="tw-mt-8 tw-pt-5 tw-border-t tw-border-charcoal-15 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                {onBack && (
                    <Button variant="outline-neutral" disabled={saving || deferring} onClick={onBack}>
                        {$t('Back')}
                    </Button>
                )}
                <Button variant="primary" loading={saving} disabled={saving || nextDisabled} onClick={onNext}>
                    {nextLabel || (position === total ? $t('Finish setup') : $t('Save and continue'))}
                </Button>
                {step.deferrable && (
                    <Button variant="outline" loading={deferring} disabled={deferring} onClick={() => onDefer(step.key)}>
                        {$t('Do this later')}
                    </Button>
                )}
                <span className="tw-ms-auto tw-text-xs tw-text-charcoal-50">{labels.hint}</span>
            </div>
        </section>
    );
}
